import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../redux/slices/authSlice';

const IDLE_TIMEOUT = 60 * 60 * 1000; // 1 dakika (test için)
const WARNING_TIME = 10 * 1000; // 10 saniye
const CHECK_INTERVAL = 1000; // Her saniye kontrol et

export const useIdleTimeout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);

  const handleLogout = useCallback(() => {
    Promise.resolve().then(() => {
      setShowWarning(false);
      setCountdown(10);
      dispatch(logout());
      navigate('/signin');
    });
  }, [dispatch, navigate]);

  // Geri sayım için ayrı bir useEffect
  useEffect(() => {
    let countdownTimer = null;

    if (showWarning) {
      countdownTimer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownTimer);
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownTimer) {
        clearInterval(countdownTimer);
      }
    };
  }, [showWarning, handleLogout]);

  useEffect(() => {
    if (!isAuthenticated) return;

    let lastActivity = Date.now();
    let countdownIntervalId = null;
    let checkIntervalId = null;

    const resetTimers = () => {
      if (checkIntervalId) {
        clearInterval(checkIntervalId);
        checkIntervalId = null;
      }

      if (countdownIntervalId) {
        clearInterval(countdownIntervalId);
        countdownIntervalId = null;
      }

      setShowWarning(false);
      setCountdown(10);
      lastActivity = Date.now();

      checkIntervalId = setInterval(checkIdle, CHECK_INTERVAL);
    };

    const startWarningPhase = () => {
      if (checkIntervalId) {
        clearInterval(checkIntervalId);
        checkIntervalId = null;
      }

      setShowWarning(true);
      setCountdown(10);
    };

    const checkIdle = () => {
      const now = Date.now();
      const idleTime = now - lastActivity;

      if (idleTime >= IDLE_TIMEOUT - WARNING_TIME && !showWarning) {
        startWarningPhase();
      }
    };

    checkIntervalId = setInterval(checkIdle, CHECK_INTERVAL);

    const updateLastActivity = () => {
      lastActivity = Date.now();

      if (showWarning) {
        resetTimers();
      }
    };

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];

    events.forEach((event) => {
      document.addEventListener(event, updateLastActivity);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, updateLastActivity);
      });
      if (checkIntervalId) {
        clearInterval(checkIntervalId);
      }
      if (countdownIntervalId) {
        clearInterval(countdownIntervalId);
      }
    };
  }, [isAuthenticated, showWarning, handleLogout]);

  return {
    showWarning,
    countdown,
    onExtendSession: () => {
      const event = new Event('mousemove');
      document.dispatchEvent(event);
    },
    onCloseWarning: handleLogout,
  };
};
